import { useChatBot } from '@/hooks/useChatBot'
import { ChatMessage } from './ChatMessage'
import { Button } from '@/components/ui/button'
import {
  CardHeader,
  CardTitle,
  CardContent,
  CardFooter,
} from '@/components/ui/card'
import { ScrollArea } from '@/components/ui/scroll-area'
import { Input } from '@/components/ui/input'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import {
  MessageCircle,
  X,
  Send,
  Bot,
  ChevronDown,
  ExternalLink,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import { useState, useEffect } from 'react'

export function ChatWidget() {
  const {
    isOpen,
    setIsOpen,
    messages,
    state,
    isTyping,
    handleOptionSelect,
    handleTextInput,
    getOptions,
    scrollRef,
    scrollToBottom,
    colors,
    ctaConfig,
    handleCtaClick,
  } = useChatBot()

  const [inputText, setInputText] = useState('')

  // Scroll logic for various events
  useEffect(() => {
    if (isOpen) {
      setTimeout(scrollToBottom, 150)
    }
  }, [isOpen, scrollToBottom])

  useEffect(() => {
    scrollToBottom()
  }, [isTyping, scrollToBottom])

  const handleSend = () => {
    if (!inputText.trim()) return
    handleTextInput(inputText)
    setInputText('')
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') handleSend()
  }

  const currentOptions = getOptions()
  const showInput =
    state.mode === 'exploration' ||
    [
      'CAPTURE_NAME',
      'CAPTURE_COMPANY',
      'CAPTURE_EMAIL',
      'CAPTURE_PHONE',
    ].includes(state.step)
  const showOptions = currentOptions.length > 0 && !isTyping

  // Additional scroll trigger when options appear
  useEffect(() => {
    if (showOptions) {
      scrollToBottom()
    }
  }, [showOptions, scrollToBottom])

  // Dynamic Styles
  const dynamicStyle = {
    '--chat-primary': colors.primary || '#4F46E5', // Default Indigo
    '--chat-secondary': colors.secondary || '#E0E7FF', // Default Indigo Light
  } as React.CSSProperties

  return (
    <div
      className="fixed bottom-4 right-4 sm:bottom-6 sm:right-6 z-[60] flex flex-col items-end gap-4 font-sans pointer-events-none"
      style={dynamicStyle}
    >
      {/* Chat Window */}
      <div
        className={cn(
          'pointer-events-auto transition-all duration-300 origin-bottom-right shadow-2xl rounded-2xl overflow-hidden bg-white border border-slate-200 flex flex-col',
          // Desktop Dimensions
          'sm:w-[380px] sm:h-[600px] sm:max-h-[calc(100vh-120px)]',
          // Mobile Dimensions (Responsive)
          'w-[calc(100vw-32px)] h-[calc(100dvh-100px)]',
          // Animation States
          isOpen
            ? 'opacity-100 scale-100 translate-y-0 mb-2'
            : 'opacity-0 scale-95 translate-y-4 pointer-events-none invisible h-0',
        )}
      >
        <div
          className="p-4 flex flex-row items-center justify-between shrink-0"
          style={{ backgroundColor: 'var(--chat-primary)', color: '#ffffff' }}
        >
          <div className="flex items-center gap-3">
            <div className="relative">
              <Avatar className="h-10 w-10 border-2 border-white/20 bg-white/10">
                <AvatarImage src="https://img.usecurling.com/i?q=robot&shape=circle&color=white" />
                <AvatarFallback className="bg-white/10 text-white">
                  <Bot size={20} />
                </AvatarFallback>
              </Avatar>
              <span className="absolute bottom-0 right-0 w-3 h-3 bg-green-500 border-2 border-slate-900 rounded-full"></span>
            </div>
            <div>
              <CardTitle className="text-base font-bold text-white">
                Núcleo Bot
              </CardTitle>
              <p className="text-xs text-white/80 font-normal">
                Diagnóstico IA & Suporte
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="icon"
            className="text-white/80 hover:text-white hover:bg-white/20"
            onClick={() => setIsOpen(false)}
          >
            <X size={20} />
          </Button>
        </div>

        {/* Persistent CTA Bar if Configured */}
        {ctaConfig.length > 0 && (
          <div className="bg-slate-50 border-b p-2 flex gap-2 overflow-x-auto shrink-0 scrollbar-hide">
            {ctaConfig.map((cta, idx) => (
              <a
                key={idx}
                href={cta.url}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-shrink-0"
                onClick={() => handleCtaClick(cta.label)}
              >
                <Button
                  variant="outline"
                  size="sm"
                  className="h-7 text-xs gap-1.5 bg-white border-slate-200 hover:border-[var(--chat-primary)] hover:text-[var(--chat-primary)]"
                >
                  {cta.label}
                  <ExternalLink className="w-3 h-3 opacity-50" />
                </Button>
              </a>
            ))}
          </div>
        )}

        <CardContent className="flex-1 p-0 overflow-hidden bg-slate-50 relative min-h-0">
          <ScrollArea className="h-full w-full">
            <div className="flex flex-col gap-2 p-4 pb-4">
              {messages.map((msg) => (
                <ChatMessage key={msg.id} message={msg} />
              ))}

              {isTyping && (
                <div className="flex w-full justify-start gap-2 mb-4 animate-fade-in">
                  <div
                    className="flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-white"
                    style={{ backgroundColor: 'var(--chat-primary)' }}
                  >
                    <Bot size={16} />
                  </div>
                  <div className="bg-white border border-slate-100 px-4 py-3 rounded-2xl rounded-tl-none shadow-sm flex items-center gap-1">
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.3s]"></span>
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce [animation-delay:-0.15s]"></span>
                    <span className="w-1.5 h-1.5 bg-slate-400 rounded-full animate-bounce"></span>
                  </div>
                </div>
              )}

              {/* Options Area */}
              {showOptions && (
                <div className="flex flex-col gap-2 mt-2 animate-fade-in pl-0 sm:pl-10">
                  <p className="text-xs text-slate-500 font-medium ml-1">
                    Escolha uma opção:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {currentOptions.map((opt, idx) => (
                      <Button
                        key={idx}
                        variant="outline"
                        className="bg-white hover:bg-slate-100 text-slate-700 text-sm h-auto py-2.5 px-3 whitespace-normal text-left justify-start min-h-[44px] hover:border-[var(--chat-primary)] hover:text-[var(--chat-primary)]"
                        onClick={() => handleOptionSelect(opt)}
                      >
                        {opt.label}
                      </Button>
                    ))}
                  </div>
                </div>
              )}

              {/* Invisible element to scroll to */}
              <div ref={scrollRef} className="h-px w-full" />
            </div>
          </ScrollArea>
        </CardContent>

        <CardFooter className="p-3 bg-white border-t border-slate-100 shrink-0">
          {showInput ? (
            <div className="flex w-full gap-2">
              <Input
                placeholder="Escreva a sua resposta..."
                value={inputText}
                onChange={(e) => setInputText(e.target.value)}
                onKeyDown={handleKeyDown}
                className="bg-slate-50 text-base md:text-sm focus-visible:ring-[var(--chat-primary)]"
              />
              <Button
                size="icon"
                onClick={handleSend}
                disabled={!inputText}
                style={{
                  backgroundColor: inputText
                    ? 'var(--chat-primary)'
                    : undefined,
                }}
              >
                <Send className="h-4 w-4" />
              </Button>
            </div>
          ) : (
            <div className="w-full text-center">
              <p className="text-xs text-slate-400 italic">
                {state.mode === 'completed'
                  ? 'Conversa terminada.'
                  : 'Selecione uma opção acima para continuar.'}
              </p>
            </div>
          )}
        </CardFooter>
      </div>

      {/* Floating Toggle Button */}
      <Button
        size="icon"
        className={cn(
          'pointer-events-auto h-12 w-12 sm:h-14 sm:w-14 rounded-full shadow-lg transition-all duration-300 hover:scale-105 hover:shadow-xl',
          isOpen
            ? 'bg-slate-800 hover:bg-slate-900 rotate-90'
            : 'animate-bounce-subtle',
        )}
        style={{ backgroundColor: !isOpen ? 'var(--chat-primary)' : undefined }}
        onClick={() => setIsOpen(!isOpen)}
      >
        {isOpen ? (
          <ChevronDown className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
        )}
      </Button>
    </div>
  )
}
